import { DentalbookLogo } from "@/components/DentalbookLogo";
import { cn } from "@/lib/cn";

type Props = {
  /** Texto opcional sob o logo. */
  label?: string;
  className?: string;
  /**
   * `entrada` — chegada na plataforma: logo grande, halo e respiração. É o momento de boas-vindas.
   * `interno` — já está dentro e a página recarregou (trocou de organização, F5): mesma identidade,
   *   mas compacta e sem fundo de destaque, porque aqui é espera, não cerimônia.
   */
  variant?: "entrada" | "interno";
};

/**
 * Loading screen SOTA — logo DentalBook no centro com:
 *  1. Pulso sutil (escala + opacidade) simulando respiração
 *  2. Anel de gradient cônico rotacionando ao redor (cria o halo vibrante)
 *  3. Ring externo esmaecido fixo como "trilho" do movimento
 *  4. Label com dots animados (3 pontos que sobem em sequência)
 *
 * Design inspirado em Linear / Amie / Vercel — minimalista mas com energia.
 */
export function LoadingScreen({ label = "Carregando", className, variant = "entrada" }: Props) {
  const interno = variant === "interno";
  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex flex-col items-center justify-center",
        interno
          ? "bg-white dark:bg-background"
          : "bg-gradient-to-br from-white via-white to-brand-50/40 dark:from-background dark:via-background dark:to-primary/5",
        "animate-[fade-in_200ms_ease-out]",
        className,
      )}
    >
      <div
        className={cn(
          "relative flex items-center justify-center",
          interno ? "h-14 w-14" : "h-24 w-24",
        )}
      >
        {/* Ring "trilho" esmaecido fixo — dá profundidade ao halo rotativo */}
        <div className="absolute inset-0 rounded-full border-[3px] border-brand-100 dark:border-primary/15" />

        {/* Halo rotativo — conic gradient mascarado em formato de arco */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "conic-gradient(from 0deg, transparent 0deg, transparent 200deg, #9ebbff 280deg, #2457ff 340deg, #1d46d6 360deg)",
            WebkitMask:
              "radial-gradient(circle, transparent 58%, #000 62%, #000 100%)",
            mask: "radial-gradient(circle, transparent 58%, #000 62%, #000 100%)",
            animation: "bd-spin 1.1s linear infinite",
          }}
          aria-hidden
        />

        {/* Logo central com pulso (breathing) — no modo interno fica parado: já se viu a animação
            de entrada, repetir a respiração a cada recarga vira ruído. */}
        <div
          className={cn(
            "relative z-10 flex items-center justify-center",
            interno ? "h-8 w-8" : "h-14 w-14",
          )}
          style={interno ? undefined : { animation: "bd-breathe 1.6s ease-in-out infinite" }}
        >
          <DentalbookLogo className={interno ? "h-7 w-7" : "h-12 w-12"} />
        </div>
      </div>

      {label && (
        <p
          className={cn(
            "flex items-baseline tracking-wide",
            interno
              ? "mt-4 text-[12px] font-medium text-zinc-500 dark:text-muted-foreground"
              : "mt-6 text-[13px] font-semibold text-zinc-700 dark:text-foreground",
          )}
        >
          {label}
          <span
            className="ml-1 inline-block"
            style={{ animation: "bd-dots 1.4s steps(4) infinite" }}
          >
            ...
          </span>
        </p>
      )}

      {/* Keyframes injetados inline pra componente ficar self-contained
          (disponíveis mesmo se o tailwind.config não incluir). */}
      <style>{`
        @keyframes bd-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes bd-breathe {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.06); filter: brightness(1.12); }
        }
        @keyframes bd-dots {
          0% { clip-path: inset(0 100% 0 0); }
          25% { clip-path: inset(0 66% 0 0); }
          50% { clip-path: inset(0 33% 0 0); }
          75%, 100% { clip-path: inset(0 0 0 0); }
        }
      `}</style>
    </div>
  );
}
